import React from 'react'
import { useTranslation } from 'react-i18next'
import { FILTER_COLOR_SCHEMES } from './PropertyFilters'

interface PropertyPaginationProps {
  currentPage: number
  totalPages: number
  perPage: number
  onPageChange: (page: number) => void
  onPerPageChange: (perPage: number) => void
  colorSchemeKey?: keyof typeof FILTER_COLOR_SCHEMES
}

const PropertyPagination: React.FC<PropertyPaginationProps> = ({
  currentPage,
  totalPages,
  perPage,
  onPageChange,
  onPerPageChange,
  colorSchemeKey = 'gold',
}) => {
  const { t } = useTranslation('properties')
  const cs = FILTER_COLOR_SCHEMES[colorSchemeKey]
  const btnActive = `${cs.primary} ${cs.primaryText} shadow-md`
  const btnInactive = `${cs.btnInactive} ${cs.btnInactiveHover}`

  return (
    <div className="mt-10 space-y-4">
      {/* Selector por página */}
      <div className="flex items-center justify-center gap-3">
        <span className={`text-sm ${cs.labelColor}`}>{t('show')}</span>
        {[6, 9, 12, 0].map((n) => (
          <button
            key={n}
            onClick={() => { onPerPageChange(n); onPageChange(1) }}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-300 ${
              perPage === n ? btnActive : btnInactive
            }`}
          >
            {n === 0 ? t('all') : n}
          </button>
        ))}
      </div>

      {/* Paginación */}
      {perPage > 0 && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${btnInactive} disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            {t('previous')}
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`w-9 h-9 rounded-full text-sm font-medium transition-all duration-300 ${
                currentPage === page ? btnActive : btnInactive
              }`}
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${btnInactive} disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            {t('next')}
          </button>
        </div>
      )}
    </div>
  )
}

export default PropertyPagination
